import type { TFunction } from 'react-i18next'
import type { FetchResult } from '@apollo/client'
import type { Dispatch } from '@reduxjs/toolkit'
import type { Business, User } from '@pbt/pbt-ui-components'

import type {
  InitiateReverseTransactionMutation,
  InitiateReverseTransactionMutationVariables,
} from '~/api/graphql/generated/types'

import { triggerErrorDoggo } from '../../../add-payment-dialog-v2/utils/errorDoggoUtils'
import { extractBackendErrorMessage } from '../../../add-payment-dialog-v2/utils/paymentDialogUtils'

interface InitiateReverseTransactionDependencies {
  currentBusiness?: Business | null
  currentUser?: User | null
  initiateReverseTransactionMutation: (options: {
    variables: InitiateReverseTransactionMutationVariables
  }) => Promise<FetchResult<InitiateReverseTransactionMutation>>
  originalPaymentId: string
  reduxDispatch: Dispatch
  setIsLoading: (isLoading: boolean) => void
  setTransactionId: (transactionId: string) => void
  t: TFunction<'Common'>
}

interface ReverseTransactionHandlerDependencies {
  currentBusiness?: Business | null
  currentUser?: User | null
  dispatch: Dispatch
  onClose: () => void
  onOk: () => void
  setIsLoading: (isLoading: boolean) => void
  setTransactionId: (transactionId: string) => void
  t: TFunction<'Common'>
}

/**
 * Helper to get the reverse initiation failure message
 */
export const getReverseInitiationFailureMessage = (
  t: TFunction<'Common'>,
): string => t('Common:PAYMENTS.REVERSE_INITIATION_FAILURE')

/**
 * Handles the reverse transaction mutation with error handling.
 * Initiates a reverse transaction for payments made through the new payment terminal flow (paymentTxId).
 * On success, sets the transaction ID for subscription listening.
 * On failure, triggers error doggo and resets loading state.
 */
export const initiateReverseTransaction = async (
  deps: InitiateReverseTransactionDependencies,
): Promise<boolean> => {
  const {
    currentBusiness,
    currentUser,
    initiateReverseTransactionMutation,
    originalPaymentId,
    reduxDispatch,
    setIsLoading,
    setTransactionId,
    t,
  } = deps

  const defaultErrorMessage = getReverseInitiationFailureMessage(t)

  try {
    const result = await initiateReverseTransactionMutation({
      variables: {
        input: {
          originalPaymentId,
        },
      },
    })

    if (!result?.data?.initiateReverseTransaction || result.errors) {
      // Extract backend error from GraphQL errors if available
      const backendErrorMessage =
        result?.errors?.[0]?.message || defaultErrorMessage
      triggerErrorDoggo(
        reduxDispatch,
        backendErrorMessage,
        500,
        currentUser,
        currentBusiness,
      )
      setIsLoading(false)
      return false
    }

    setTransactionId(result.data.initiateReverseTransaction.id)
    return true
  } catch (err) {
    // Use extractBackendErrorMessage to get the most specific error available
    const backendErrorMessage = extractBackendErrorMessage(
      err,
      defaultErrorMessage,
    )
    triggerErrorDoggo(
      reduxDispatch,
      backendErrorMessage,
      500,
      currentUser,
      currentBusiness,
    )
    setIsLoading(false)
    return false
  }
}

/**
 * Creates a success handler for reverse transaction subscription results.
 * Resets loading state, clears transaction ID, and triggers success callbacks.
 */
export const createReverseSuccessHandler = (
  deps: ReverseTransactionHandlerDependencies,
): (() => void) => {
  const { setIsLoading, setTransactionId, onOk, onClose } = deps

  return (): void => {
    setIsLoading(false)
    setTransactionId('')
    onOk()
    onClose()
  }
}

/**
 * Creates an error handler for reverse transaction subscription results.
 * Displays error message, resets loading state, and clears transaction ID.
 */
export const createReverseErrorHandler = (
  deps: ReverseTransactionHandlerDependencies,
): ((customMessage?: string) => void) => {
  const {
    currentBusiness,
    currentUser,
    dispatch,
    setIsLoading,
    setTransactionId,
    t,
  } = deps

  return (customMessage?: string): void => {
    const errorMessage = customMessage || getReverseInitiationFailureMessage(t)
    triggerErrorDoggo(dispatch, errorMessage, 500, currentUser, currentBusiness)
    setIsLoading(false)
    setTransactionId('')
  }
}
